import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box, Grid, Typography, Paper, Button, Alert, Dialog, DialogTitle, DialogContent,
    List, ListItem, ListItemText, CircularProgress, Chip, DialogActions, Divider,
    TextField, Autocomplete, Snackbar, IconButton, LinearProgress, Avatar
} from "@mui/material";
import { styled } from "@mui/system";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { db } from "../firebase";
import { 
    collection, getDocs, query, where, addDoc, doc, 
    updateDoc, deleteDoc, increment, getDoc 
} from "firebase/firestore";

// Icone
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';

// Configurazione Locale
dayjs.locale('it');
const dayMap = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// --- STYLED COMPONENTS ---
const MainContainer = styled(Box)({
    minHeight: "100vh",
    background: "#F8F9FA",
    padding: "30px",
    fontFamily: "'Inter', sans-serif",
});

const ModernCard = styled(Paper)({
    background: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
    padding: "24px",
    marginBottom: "24px",
    overflow: "visible"
});

const SectionTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#2d3436",
    marginBottom: "20px",
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
});

const SlotCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'full' && prop !== 'selected'
})(({ selected, full }) => ({
    position: 'relative',
    background: "#fff",
    border: selected ? "2px solid #a32b2b" : "1px solid #f1f2f6",
    borderRadius: "16px",
    padding: "20px",
    cursor: full ? "not-allowed" : "pointer",
    opacity: full ? 0.6 : 1,
    transition: "all 0.2s ease",
    boxShadow: selected ? "0 8px 20px rgba(163, 43, 43, 0.15)" : "none",
    "&:hover": {
        transform: full ? "none" : "translateY(-4px)",
        boxShadow: full ? "none" : "0 10px 25px rgba(0,0,0,0.05)",
        borderColor: full ? "#f1f2f6" : (selected ? "#a32b2b" : "#dfe6e9")
    }
}));

const CapacityBar = styled(LinearProgress)(({ value }) => ({
    height: 8,
    borderRadius: 5,
    backgroundColor: '#f1f2f6',
    marginTop: '12px',
    '& .MuiLinearProgress-bar': {
        borderRadius: 5,
        backgroundColor: value >= 100 ? '#ff4757' : (value >= 70 ? '#ffa502' : '#2ed573'),
    },
}));

export default function BookingPage() {
    const [users, setUsers] = useState([]);
    const [allActivitySlots, setAllActivitySlots] = useState([]);
    const [user, setUser] = useState("");
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedSlotId, setSelectedSlotId] = useState("");
    
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });
    const [submitting, setSubmitting] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);

    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [detailSlot, setDetailSlot] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, "users"));
            const usersData = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
                displayName: doc.data().fullName || doc.data().email || doc.id
            }));
            setUsers(usersData);
        } catch (err) { console.error(err); }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    useEffect(() => {
        const fetchSlots = async () => {
            try {
                const slotsSnapshot = await getDocs(collection(db, "activitySlots"));
                const slotsData = slotsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                slotsData.sort((a, b) => a.startTime.localeCompare(b.startTime));
                setAllActivitySlots(slotsData);
            } catch (err) { console.error(err); }
        };
        fetchSlots();
    }, []);

    const fetchBookings = useCallback(async (date) => {
        setLoading(true);
        const dateStr = date.format("YYYY-MM-DD");
        try {
            const q = query(collection(db, "userBookings"), where("dataPrenotazione", "==", dateStr));
            const snapshot = await getDocs(q);
            const validBookings = snapshot.docs
                .map(doc => ({ bookingId: doc.id, ...doc.data() }))
                .filter(data => data.status === "ACTIVE" || !data.status);

            setBookings(validBookings);
        } catch (err) {
            setFeedback({ open: true, message: 'Errore caricamento prenotazioni.', severity: 'error' });
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchBookings(selectedDate); }, [selectedDate, fetchBookings]);

    const availableSlotsForDay = useMemo(() => {
        if (allActivitySlots.length === 0) return [];
        const dayName = dayMap[selectedDate.day()];
        return allActivitySlots.filter(slot => slot.day === dayName);
    }, [selectedDate, allActivitySlots]);

    const slotCounts = useMemo(() => {
        const counts = {};
        bookings.forEach(b => { 
            let cleanId = b.activitySlotId;
            const parts = b.activitySlotId.split("-");
            if (parts.length >= 3) {
                cleanId = parts.slice(0, parts.length - 2).join("-");
            }
            counts[cleanId] = (counts[cleanId] || 0) + 1; 
        });
        return counts;
    }, [bookings]);

    const handleDetailClick = (slot, event) => {
        event.stopPropagation();
        const participants = bookings
            .filter(b => b.activitySlotId === slot.id || b.activitySlotId.startsWith(slot.id + "-"))
            .map(b => {
                const userData = users.find(u => u.id === b.userId);
                return { 
                    bookingId: b.bookingId, 
                    userId: b.userId, 
                    displayName: userData ? userData.displayName : `ID: ${b.userId} (Utente non trovato)` 
                };
            });

        setDetailSlot({ ...slot, currentCount: participants.length, participants });
        setDetailDialogOpen(true);
    };

    const handleCancelBooking = async (bookingId, userId) => {
        if (!window.confirm("Cancellare la prenotazione? Il credito verrà riaccreditato se l'utente ha un Carnet.")) return;
        setSubmitting(true);
        try {
            const userRef = doc(db, "users", userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists() && userSnap.data().tipoAbbonamento === 'Carnet') {
                await updateDoc(userRef, { ingressiResidui: increment(1) });
            }
            await deleteDoc(doc(db, "userBookings", bookingId));
            setFeedback({ open: true, message: 'Prenotazione rimossa.', severity: 'success' });
            setDetailDialogOpen(false);
            await fetchBookings(selectedDate);
            await fetchUsers();
        } catch (err) {
            setFeedback({ open: true, message: 'Errore cancellazione.', severity: 'error' });
        } finally { setSubmitting(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user || !selectedSlotId) return setFeedback({ open: true, message: 'Seleziona Utente e Slot.', severity: 'warning' });
        const slotToBook = availableSlotsForDay.find(slot => slot.id === selectedSlotId);
        const userData = users.find(u => u.id === user);

        if (userData?.tipoAbbonamento === 'Carnet' && (userData.ingressiResidui || 0) <= 0) {
            return setFeedback({ open: true, message: 'Ingressi insufficienti!', severity: 'error' });
        }

        const compositeSlotId = `${selectedSlotId}-${slotToBook.startTime}-${slotToBook.endTime}`;
        if ((slotCounts[selectedSlotId] || 0) >= slotToBook.maxCapacity) {
            return setFeedback({ open: true, message: 'Slot Pieno.', severity: 'error' });
        }
        
        setSubmitting(true);
        try {
            if (userData?.tipoAbbonamento === 'Carnet') {
                await updateDoc(doc(db, "users", user), { ingressiResidui: increment(-1) });
            }
            await addDoc(collection(db, "userBookings"), {
                userId: user,
                activitySlotId: compositeSlotId,
                dataPrenotazione: selectedDate.format("YYYY-MM-DD"),
                oraPrenotazione: `${slotToBook.startTime}-${slotToBook.endTime}`,
                tipoAttivita: slotToBook.courseName,
                bookedAt: new Date(),
                status: "ACTIVE",
            });
            setFeedback({ open: true, message: '✅ Prenotazione confermata!', severity: 'success' });
            setSelectedSlotId(""); 
            await fetchBookings(selectedDate);
            await fetchUsers();
        } catch (err) { setFeedback({ open: true, message: 'Errore salvataggio.', severity: 'error' }); } 
        finally { setSubmitting(false); }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <MainContainer>
                <Box sx={{ mb: 4 }}>
                     <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>Prenotazione Admin</Typography>
                     <Typography variant="body2" color="text.secondary">Gestione manuale prenotazioni e crediti carnet.</Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <ModernCard>
                        <SectionTitle><PersonSearchIcon color="primary"/> Utente e Data</SectionTitle>
                        <Grid container spacing={3} alignItems="center">
                            {/* --- FIX: AUTOCOMPLETE RIPRISTINATO --- */}
                            <Grid item xs={12} md={8} sx={{ width: '100%' }}>
                                <Autocomplete
                                    fullWidth
                                    disablePortal
                                    options={users}
                                    getOptionLabel={(option) => {
                                        const residui = option.ingressiResidui !== undefined ? option.ingressiResidui : 0;
                                        return `${option.displayName} ${option.tipoAbbonamento === 'Carnet' ? `(Ingressi: ${residui})` : ''}`;
                                    }}
                                    value={users.find(u => u.id === user) || null}
                                    onChange={(event, newValue) => setUser(newValue ? newValue.id : "")}
                                    sx={{ width: '100%', minWidth: { xs: '100%', md: '350px' } }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Cerca Utente" 
                                            placeholder="Digita nome..."
                                            variant="outlined" 
                                            fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }} 
                                        />
                                    )}
                                />
                            </Grid>
                            {/* ------------------------------------- */}
                            <Grid item xs={12} md={4}>
                                <DatePicker
                                    label="Seleziona Data"
                                    value={selectedDate}
                                    onChange={(newDate) => { setSelectedDate(newDate); setSelectedSlotId(""); }}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { fullWidth: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
                                />
                            </Grid>
                        </Grid>
                    </ModernCard>

                    <ModernCard>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <SectionTitle sx={{ mb: 0 }}><CalendarMonthIcon color="primary"/> Slot Disponibili</SectionTitle>
                            <Chip label={selectedDate.format("dddd DD MMMM")} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                        </Box>

                        {loading ? (
                             <Box sx={{ textAlign: "center", p: 4 }}><CircularProgress sx={{ color: '#a32b2b' }} /></Box>
                        ) : availableSlotsForDay.length === 0 ? (
                            <Alert severity="info" sx={{ borderRadius: '12px' }}>Nessun corso programmato.</Alert>
                        ) : (
                            <Grid container spacing={3}>
                                {availableSlotsForDay.map((slot) => {
                                    const count = slotCounts[slot.id] || 0;
                                    const max = slot.maxCapacity;
                                    const isFull = count >= max;
                                    const isSelected = selectedSlotId === slot.id;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={slot.id}>
                                            <SlotCard 
                                                selected={isSelected} 
                                                full={isFull} 
                                                onClick={() => !isFull && setSelectedSlotId(isSelected ? "" : slot.id)}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Chip label={`${slot.startTime} - ${slot.endTime}`} size="small" sx={{ bgcolor: isSelected ? '#a32b2b' : '#f1f2f6', color: isSelected ? '#fff' : '#2d3436', fontWeight: 'bold' }} />
                                                    <IconButton size="small" onClick={(e) => handleDetailClick(slot, e)} sx={{ p: 0.5 }}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant="h6" fontWeight="bold">{slot.courseName}</Typography>
                                                <Typography variant="body2" color="text.secondary">Istruttore: {slot.instructor || 'Staff'}</Typography>
                                                <Box sx={{ mt: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', mb: 0.5 }}>
                                                        <span>Occupazione</span>
                                                        <strong>{count} / {max}</strong>
                                                    </Box>
                                                    <CapacityBar variant="determinate" value={(count/max)*100} />
                                                </Box>
                                                {isSelected && (
                                                    <Box sx={{ position: 'absolute', top: -10, right: -10, bgcolor: '#a32b2b', borderRadius: '50%', p: 0.5, boxShadow: 2 }}>
                                                        <CheckCircleIcon sx={{ color: '#fff', fontSize: 20 }} />
                                                    </Box>
                                                )}
                                            </SlotCard>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </ModernCard>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !selectedSlotId || !user}
                            startIcon={<EventAvailableIcon />}
                            sx={{ minWidth: 250, py: 1.5, borderRadius: '12px', bgcolor: '#a32b2b', '&:hover': { bgcolor: '#802222' } }}
                        >
                            {submitting ? 'Elaborazione...' : 'CONFERMA PRENOTAZIONE'}
                        </Button>
                    </Box>
                </Box>

                <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px' } }} maxWidth="xs" fullWidth>
                    {detailSlot && (
                        <>
                            <DialogTitle>
                                {detailSlot.courseName}
                                <Typography variant="caption" display="block" color="text.secondary">{detailSlot.startTime} - {detailSlot.endTime}</Typography>
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="subtitle2" sx={{ mb: 2, bgcolor: '#f1f2f6', p: 1, borderRadius: 1 }}>Partecipanti ({detailSlot.participants.length})</Typography>
                                <List dense>
                                    {detailSlot.participants.map((p, i) => (
                                        <ListItem 
                                            key={i} 
                                            divider={i < detailSlot.participants.length - 1}
                                            secondaryAction={
                                                <IconButton edge="end" color="error" onClick={() => handleCancelBooking(p.bookingId, p.userId)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            }
                                        >
                                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 2 }}>{p.displayName.charAt(0)}</Avatar>
                                            <ListItemText primary={p.displayName} />
                                        </ListItem>
                                    ))}
                                </List>
                            </DialogContent>
                            <DialogActions><Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button></DialogActions>
                        </>
                    )}
                </Dialog>

                <Snackbar open={feedback.open} autoHideDuration={4000} onClose={() => setFeedback(prev => ({...prev, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={feedback.severity} variant="filled" sx={{ borderRadius: 2 }}>{feedback.message}</Alert>
                </Snackbar>
            </MainContainer>
        </LocalizationProvider>
    );
}